import type { AnalyticsReport } from "@server/types/analyticsReportTypes";
import { useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  formatRequests,
  formatXAxisLabel,
  type Granularity,
} from "../utils/formatMetric";
import { RequestVolumeTooltip } from "./RequestVolumeTooltip";
import { SpendChart } from "./SpendChart";

const config = {
  successRequests: { label: "Successful", color: "var(--chart-1)" },
  errors: { label: "HTTP errors", color: "var(--chart-5)" },
};

/** Shows overall request volume and settled spend for the selected report range. */
export function AnalyticsCharts({
  report,
  granularity,
}: {
  report: AnalyticsReport;
  granularity: Granularity;
}) {
  const [volumeTooltipPosition, setVolumeTooltipPosition] = useState<
    { x: number; y: number } | undefined
  >();
  const data = report.timeseries.map((item) => ({
    ...item,
    successRequests: item.requests - item.errors,
  }));
  return (
    <div className="analytics-charts">
      <Card className="analytics-panel request-volume-panel">
        <CardHeader className="analytics-chart-heading">
          <CardTitle>Request volume</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="analytics-legend">
            {Object.values(config).map(({ label, color }) => (
              <span key={label}>
                <i style={{ background: color }} />
                {label}
              </span>
            ))}
          </div>
          <ChartContainer
            config={config}
            onMouseMove={(event) => {
              const chart = event.currentTarget;
              const bounds = chart.getBoundingClientRect();
              const tooltip = chart.querySelector<HTMLElement>(
                ".request-volume-breakdown",
              );
              const width =
                tooltip?.getBoundingClientRect().width ??
                Math.min(280, window.innerWidth - 48);
              const right = event.clientX + 16;
              const left =
                right + width <= window.innerWidth - 8
                  ? right
                  : event.clientX - width - 16;
              setVolumeTooltipPosition({
                x:
                  Math.max(8, Math.min(left, window.innerWidth - width - 8)) -
                  bounds.left,
                y: 0,
              });
            }}
            className="h-[200px] w-full aspect-auto"
          >
            <BarChart
              data={data}
              margin={{ top: 16, right: 8, left: -12, bottom: 0 }}
              accessibilityLayer
              barCategoryGap="10%"
              barGap={1}
            >
              <CartesianGrid
                vertical={false}
                stroke="var(--border)"
                strokeDasharray="3 5"
              />
              <XAxis
                dataKey="timestamp"
                axisLine={false}
                tickLine={false}
                minTickGap={32}
                tickMargin={12}
                tickFormatter={(timestamp) =>
                  formatXAxisLabel({ timestamp, granularity })
                }
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => formatRequests({ n: value })}
                width={72}
                allowDecimals={false}
              />
              <ChartTooltip
                wrapperStyle={{ zIndex: 50, pointerEvents: "none" }}
                position={volumeTooltipPosition}
                isAnimationActive={false}
                offset={16}
                content={(props) => (
                  <RequestVolumeTooltip
                    active={props.active}
                    timestamp={String(props.label ?? "")}
                    report={report}
                  />
                )}
              />
              <Bar
                dataKey="successRequests"
                stackId="requests"
                fill="var(--color-successRequests)"
                maxBarSize={36}
                isAnimationActive={false}
              />
              <Bar
                dataKey="errors"
                stackId="requests"
                fill="var(--color-errors)"
                radius={[3, 3, 0, 0]}
                maxBarSize={36}
                isAnimationActive={false}
              />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
      <SpendChart spend={report.spend} granularity={granularity} />
    </div>
  );
}
